import {
  createTheme,
  lighten,
  type SkeletonProps,
  type ThemeOptions,
} from '@mui/material'

import { customCommonColors, spacingConstants } from '../constants'

import { commonThemeOptions } from './commonThemeOptions'

type CustomThemeOptions = ThemeOptions & {
  props: {
    MuiSkeleton: SkeletonProps
  }
}

const hubloThemeOptions: Partial<CustomThemeOptions> = {
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          paddingTop: 'env(safe-area-inset-top);',
          paddingBottom: 'env(safe-area-inset-bottom);',
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiSelect: {
      defaultProps: {
        MenuProps: {
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        },
      },
      styleOverrides: {
        select: {
          boxSizing: 'border-box',
          height: '56px',
          alignItems: 'center',
          display: 'flex',
          '&:focus': {
            backgroundColor: lighten(customCommonColors.grey0, 0.6),
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiButton: {
      defaultProps: {
        disableFocusRipple: true,
      },
    },
    MuiSkeleton: {
      defaultProps: {
        animation: 'pulse',
      },
      styleOverrides: {
        root: {
          backgroundColor: customCommonColors.grey5,
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        enterTouchDelay: 250,
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: customCommonColors.black,
          color: customCommonColors.white,
          fontSize: '12px',
          lineHeight: '20px',
        },
        arrow: {
          color: customCommonColors.black,
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '&.Mui-BottomActionBar': {
            borderTop: `1px solid ${customCommonColors.grey0}`,
            boxShadow: `0px -2px 4px rgba(234, 241, 248, 0.3)`,
            backgroundColor: customCommonColors.white,
            padding: 0,
            maxWidth: 'unset',
            '&>.Mui-ButtonWrapper': {
              maxWidth: 568,
              height: 'unset',
              minHeight: 64,
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap-reverse',
              margin: 'auto',
              gap: `${spacingConstants.global}px ${
                spacingConstants.global * 2
              }px`,
              padding: `${spacingConstants.global}px ${
                spacingConstants.global * 2
              }px`,
              '&>.MuiButtonBase-root': {
                flex: `1 1 calc(50% - ${spacingConstants.global}px)`,
                whiteSpace: 'nowrap',
                minWidth: 'unset',
              },
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: customCommonColors.grey0,
          },
          '&.MuiTouchRipple-root': {
            backgroundColor: customCommonColors.grey5,
          },
          color: customCommonColors.black,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: `12px ${spacingConstants.global * 2}px`,
          height: 48,
          '&.Mui-disabled': {
            color: customCommonColors.grey10,
            opacity: 1,
          },
          '&:hover': {
            backgroundColor: customCommonColors.grey0,
            '&.Mui-disabled': {
              backgroundColor: 'unset',
            },
          },
          '&$selected': {
            backgroundColor: customCommonColors.blue0,
            '&:hover': {
              backgroundColor: customCommonColors.blue5,
            },
          },
          '&>.MuiIconButton-root': {
            padding: '0 12px 0 0',
            '&:hover': {
              backgroundColor: 'unset',
            },
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        popper: {
          "& $option[data-focus='true']": {
            backgroundColor: lighten(customCommonColors.grey0, 0.6),
          },
          "& $option[aria-selected='true']": {
            backgroundColor: customCommonColors.grey0,
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          '&.MuiAvatarSize-xl': {
            width: '56px',
            height: '56px',
            '&>.MuiSvgIcon-root': {
              width: '32px',
              height: '32px',
            },
          },
          '&.MuiAvatarSize-lg': {
            width: '48px',
            height: '48px',
            '&>.MuiSvgIcon-root': {
              width: '32px',
              height: '32px',
            },
          },
          '&.MuiAvatarSize-md': {
            width: '40px',
            height: '40px',
            '&>.MuiSvgIcon-root': {
              width: '24px',
              height: '24px',
            },
          },
          '&.MuiAvatarSize-sm': {
            width: '32px',
            height: '32px',
            '&>.MuiSvgIcon-root': {
              width: '24px',
              height: '24px',
            },
          },
          '&.MuiAvatarSize-xs': {
            width: '24px',
            height: '24px',
            '&>.MuiSvgIcon-root': {
              width: '16px',
              height: '16px',
            },
          },
        },
        colorDefault: {
          backgroundColor: customCommonColors.grey5,
          color: customCommonColors.grey20,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          height: 40,
          fontFamily: ['Figtree', 'sans-serif'].join(','),
          textTransform: 'none',
          color: customCommonColors.grey10,
          borderRadius: '8px',
          border: `1px solid ${customCommonColors.grey5}`,

          '&$selected': {
            backgroundColor: customCommonColors.blue0,
            border: `1px solid ${customCommonColors.blue10} !important`,
            color: customCommonColors.blue15,

            '&:hover': {
              backgroundColor: customCommonColors.blue5,
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '8px',
          margin: spacingConstants.global * 2,
          padding: spacingConstants.global * 2,
        },
        paperFullScreen: {
          margin: 0,
          borderRadius: 0,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: `0 0 ${spacingConstants.global * 2}px 0`,
          '& .MuiIconButton-sizeSmall': {
            marginLeft: spacingConstants.global * 2,
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: `0 0 ${spacingConstants.global}px 0`,
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          padding: `0 0 ${spacingConstants.global * 2}px 0`,
          marginBottom: 0,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: 0,
          display: 'block',
          '&>.MuiContainer-root.Mui-BottomActionBar': {
            '&>.Mui-ButtonWrapper': {
              padding: 0,
            },
            minHeight: 48,
            border: 'unset',
            boxShadow: 'unset',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          display: 'flex',
          alignItems: 'center',
          width: 'auto',
          overflow: 'visible',
        },
        thumb: {
          height: 18,
          width: 18,
          backgroundColor: customCommonColors.white,
          margin: 6,
          boxShadow: 'unset',
        },
        track: {
          height: 24,
          width: 42,
          borderRadius: 24,
          opacity: 1,
          backgroundColor: customCommonColors.grey5,
        },
        switchBase: {
          top: 'unset',
          '&.Mui-checked': {
            transform: 'translateX(18px)',
          },
        },
        colorSecondary: {
          '&:hover, &.Mui-checked:hover': {
            backgroundColor: 'unset',
          },
          '&.Mui-checked+.MuiSwitch-track': {
            backgroundColor: customCommonColors.blue15,
            opacity: 1,
          },
          '&.Mui-checked.Mui-disabled+.MuiSwitch-track': {
            backgroundColor: customCommonColors.blue5,
          },
          '&.Mui-disabled+.MuiSwitch-track': {
            opacity: 1,
            backgroundColor: customCommonColors.grey0,
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: customCommonColors.grey5,
          marginTop: '15px',
          marginBottom: spacingConstants.global * 2,
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: customCommonColors.grey0,
          },
          '& .MuiTouchRipple-child': {
            backgroundColor: customCommonColors.grey5,
          },
          '&$checked .MuiTouchRipple-child': {
            backgroundColor: customCommonColors.blue5,
          },
          '&$checked$disabled .MuiSvgIcon-root': {
            backgroundColor: customCommonColors.blue5,
          },
          '&$disabled .MuiSvgIcon-root': {
            fill: customCommonColors.grey5,
          },
          '&$checked .MuiSvgIcon-root': {
            backgroundColor: customCommonColors.blue15,
            borderRadius: '50%',
            fill: customCommonColors.white,
            width: 20,
            height: 20,
            margin: '2px',
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          cursor: 'pointer',
          fontWeight: 'bold',

          '&.MuiLinkContainer': {
            display: 'inline-flex',
            alignItems: 'center',
          },

          '&>.MuiSvgIcon-root': {
            marginLeft: `${spacingConstants.global * 0.5}px`,
            height: 16,
            width: 16,
          },

          '&.MuiLinkColor-default': {
            color: customCommonColors.blue15,

            '&:hover': {
              color: customCommonColors.blue10,
            },
            '&:focus-visible': {
              borderRadius: '2px',
              outline: `solid 2px ${customCommonColors.black20}`,
            },
          },
          '&.MuiLinkColor-dark-background': {
            color: customCommonColors.white,
            '&:hover': {
              color: customCommonColors.blue5,
            },
            '&:focus-visible': {
              borderRadius: '2px',
              outline: `solid 2px ${customCommonColors.blue0}`,
            },
          },
          '&.MuiLinkColor-black': {
            color: customCommonColors.black20,
            textDecorationColor: customCommonColors.black20,

            '&:hover': {
              color: customCommonColors.grey15,
              textDecorationColor: customCommonColors.grey15,
            },
            '&:focus-visible': {
              borderRadius: '2px',
              outline: `solid 2px ${customCommonColors.black20}`,
            },
          },

          '&.MuiLinkSize-default': {
            fontSize: '16px',
            lineHeight: '24px',
          },
          '&.MuiLinkSize-small': {
            fontSize: '14px',
            lineHeight: '22px',
          },
          '&.MuiLinkSize-extra-small': {
            fontSize: '12px',
            lineHeight: '20px',
          },
          '&.MuiLinkSize-inherit': {
            fontSize: 'inherit',
            lineHeight: 'inherit',
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          '&.MuiSvgIcon-fontSizeXsmall': {
            fontSize: '1rem',
          },
        },
      },
    },
  },
}

export const hubloTheme = createTheme(commonThemeOptions, hubloThemeOptions)

// Never do directly "hubloTheme.something = {}" because it will erase commonThemeOptions
